import pdfMake from "../../fonts/pdfMakeConfig";
import { viewDocument } from "../utiles/DocumentService";
import { getLabelFromValue } from "../utiles/Dropdown";
import { PDFDocument } from "pdf-lib";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
const NAVY = "#1a237e";

const clean = (v) => (v === undefined || v === null ? "" : String(v).trim());

const joinParts = (parts, sep = ", ") =>
  parts.map(clean).filter(Boolean).join(sep);

const bullets = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);

const pickText = (ai, manual) =>
  ai && String(ai).trim() !== "" ? ai : manual || "";
const getObservation = (item) => pickText(item.aiObservation, item.observation);
const getSuggestion = (item) => pickText(item.aiSuggestion, item.suggestion);

export const generateAuditPDF = async ({
  isQuestion = false,
  orgData,
  auddetails,
  observationdate,
  user,
  empData,
  divCdList,
  emptypeOptions,
  ackContent,
  exSummaryContent,
  introductionContent,
  firstNote,
  PriorityContent,
  closingNotes,
  recommendation,
  conclusion,
  references,
  abbreviation,
  details,
  scheduleid,
  annextureList,
  documents,
  auditeecode,
}) => {
  const sectionPageMap = {};
  const markPage = (id, node) => {
    if (id && node.pageNumbers?.length) {
      sectionPageMap[id] = node.pageNumbers[0];
    }
  };

  const orgcode = localStorage.getItem("custCode") || "ptw";
  const envCode = localStorage.getItem("envcode") || "Prod";

  const obsSectionTitle = isQuestion
    ? "7. QUESTIONS, OBSERVATIONS & SUGGESTIONS"
    : "7. OBSERVATIONS & SUGGESTIONS";
  const obsIndexLabel = isQuestion
    ? "Questions, Observations & Suggestions"
    : "Observations & Suggestions";

  const officer = empData.find((d) => d.code === user) || {};
  const inspectionOfficerName = officer.name || user;
  const inspectionOfficerDesignation = officer.designation || "";
  const industry = JSON.parse(auddetails.industry || "{}");
  const occupiers = JSON.parse(auddetails.occupier || "[]");
  const employees = JSON.parse(auddetails.employee || "[]");
  const occ = occupiers[0] || {};

  const employeeTypeMap = Object.fromEntries(
    (emptypeOptions || []).map((opt) => [opt.value, opt.label]),
  );
  const totalMale = employees.reduce(
    (sum, e) => sum + Number(e.adultMale || 0),
    0,
  );
  const totalFemale = employees.reduce(
    (sum, e) => sum + Number(e.adultFemale || 0),
    0,
  );

  const employeeRows = [
    ...employees.map((e) => [
      employeeTypeMap[e.employeeType] || `Type ${e.employeeType}`,
      e.adultMale || "0",
      e.adultFemale || "0",
    ]),
    ["Total", totalMale.toString(), totalFemale.toString()],
  ];

  const annexurePdfList = [];
  const documentPdfList = [];

  /* ---------- Org cover lines (only if present) ---------- */
  const orgName = clean(orgData?.name);
  const orgAddressLine = joinParts([
    orgData?.addressline1,
    orgData?.city,
    orgData?.dist,
    orgData?.state,
    orgData?.country,
    orgData?.pin,
  ]);
  const orgContactLine = joinParts(
    [
      clean(orgData?.contact) && `Phone: ${clean(orgData?.contact)}`,
      clean(orgData?.emailid) && `Email: ${clean(orgData?.emailid)}`,
    ],
    "  |  ",
  );

  // ----------------------------
  // Load helpers
  // ----------------------------
  const loadAnnexureFile = async (annex) => {
    try {
      if (!annex || !annex.path || !annex.value) {
        return { type: "none", error: "Invalid annexure details" };
      }

      const { path, value } = annex;
      const folderPath = `${orgcode}/anexture_${value}`;
      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: path,
      });

      if (response?.cd === 1) {
        return { type: "none", error: "No file received" };
      }

      const base64 = response;
      const ext = path.split(".").pop().toLowerCase();

      if (["png", "jpg", "jpeg"].includes(ext)) {
        return { type: "image", data: `data:image/${ext};base64,${base64}` };
      }
      if (ext === "pdf") {
        return { type: "pdf", data: `data:application/pdf;base64,${base64}` };
      }
      return { type: "none", error: "Unsupported format" };
    } catch (err) {
      console.error("Annexure load error:", err);
      return { type: "none", error: "Failed to load annexure" };
    }
  };

  const fetchImages = async () => {
    if (!orgData?.logo) return null;
    try {
      const logoNames = orgData.logo;
      const folderPath = `${orgcode}/org_${orgData.code}`;
      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: logoNames,
      });

      if (!response || response?.cd === 1) return null;

      const mimeType = logoNames.toLowerCase().endsWith(".pdf")
        ? "application/pdf"
        : "image/jpeg";
      return {
        name: logoNames,
        url: `data:${mimeType};base64,${response}`,
      };
    } catch (err) {
      console.error("Image fetch error :", err);
      return null;
    }
  };

  const fetchObservationImages = async (imageNames = []) => {
    if (!Array.isArray(imageNames) || imageNames.length === 0) return [];
    const results = [];

    for (let fileName of imageNames) {
      try {
        const folderPath = `${orgcode}/sc_${scheduleid}`;
        const response = await viewDocument({
          projectCode: "Ptw",
          envCode,
          folderPath,
          fileName,
        });
        if (response?.cd === 1) {
          results.push(null);
          continue;
        }

        if (response) {
          const lower = fileName.toLowerCase();
          let mime = "image/png";
          if (lower.endsWith(".jpg") || lower.endsWith(".jpeg"))
            mime = "image/jpeg";
          if (lower.endsWith(".gif")) mime = "image/gif";

          results.push(`data:${mime};base64,${response}`);
        } else {
          results.push(null);
        }
      } catch (err) {
        console.error("Image fetch error for:", fileName, err);
        results.push(null);
      }
    }
    return results;
  };

  const isValidImage = (img) =>
    typeof img === "string" && img.startsWith("data:image/") && img.length > 30;

  const getObservationsContent = async () => {
    const blocks = [];
    let counter = 1;

    for (const item of details || []) {
      const obsText = getObservation(item);
      const sugText = getSuggestion(item);

      if (obsText.trim() === "" && sugText.trim() === "") continue;

      const headingText =
        isQuestion && item.question
          ? `${counter}) ${item.question}`
          : `${counter}) ${obsText}`;

      // Question mode: show observation answer and suggestion.
      // Non-question mode: show only suggestion below observation heading.
      const lines = isQuestion
        ? [
            ...(obsText.trim() ? bullets(obsText) : []),
            ...(sugText.trim() ? bullets(sugText) : []),
          ]
        : sugText.trim()
          ? bullets(sugText)
          : [];

      blocks.push({
        unbreakable: true,
        stack: [
          {
            text: headingText,
            bold: true,
            fontSize: 10,
            color: NAVY,
            margin: [0, 8, 0, 4],
          },
          ...(lines.length
            ? [{ ul: lines, fontSize: 9, margin: [8, 0, 0, 2] }]
            : []),
        ],
      });

      const images = (await fetchObservationImages(item.image || [])).filter(
        isValidImage,
      );

      images.forEach((img) =>
        blocks.push({
          image: img,
          width: 130,
          margin: [8, 3, 0, 5],
        }),
      );

      counter++;
    }

    if (counter === 1) {
      return [
        {
          text: "No observations recorded.",
          italics: true,
          color: "#666",
          margin: [0, 5, 0, 10],
        },
      ];
    }

    return blocks;
  };

  // ----------------------------
  // Documents & Annexure builders
  // ----------------------------
  const buildDocumentsSection = async (docList) => {
    if (!docList || docList.length === 0) return [];

    const grouped = {};
    for (const doc of docList) {
      const key = Number(doc.orderBy) || 0;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    }

    const blocks = [];
    const sortedOrders = Object.keys(grouped)
      .map(Number)
      .sort((a, b) => a - b);

    for (const order of sortedOrders) {
      const docs = grouped[order];
      const images = docs.filter((d) =>
        ["png", "jpg", "jpeg"].includes(
          d.fileName.split(".").pop().toLowerCase(),
        ),
      );
      const pdfs = docs.filter((d) =>
        d.fileName.toLowerCase().endsWith(".pdf"),
      );

      for (const doc of images) {
        try {
          const folderPath = `${orgcode}/auditee${auditeecode}`;
          const response = await viewDocument({
            projectCode: "Ptw",
            envCode,
            folderPath,
            fileName: doc.fileName,
          });
          if (response?.cd === 1) continue;
          const base64 = response;
          const ext = doc.fileName.split(".").pop().toLowerCase();

          blocks.push({
            text: `${order}. ${doc.fileName}`,
            bold: true,
            fontSize: 9,
            margin: [0, 6, 0, 3],
          });
          blocks.push({
            image: `data:image/${ext};base64,${base64}`,
            width: 480,
            margin: [0, 2, 0, 10],
          });
        } catch (err) {
          blocks.push({
            text: `Error loading ${doc.fileName}`,
            color: "red",
            fontSize: 8,
            italics: true,
          });
        }
      }

      for (const doc of pdfs) {
        try {
          const folderPath = `${orgcode}/auditee${auditeecode}`;
          const response = await viewDocument({
            projectCode: "Ptw",
            envCode,
            folderPath,
            fileName: doc.fileName,
          });
          if (response?.cd === 1) continue;
          const base64 = response;
          blocks.push({
            text: `${order}. ${doc.fileName} (PDF – attached)`,
            italics: true,
            fontSize: 9,
            margin: [0, 4, 0, 8],
          });
          documentPdfList.push({
            fileName: doc.fileName,
            base64,
            orderBy: order,
          });
        } catch (err) {
          console.error("PDF load error:", doc.fileName, err);
        }
      }
    }
    return blocks;
  };

  const buildAnnexureContent = async (annexList) => {
    if (!annexList || annexList.length === 0) return [];

    const blocks = [];
    let imageIndex = 1;

    for (const annex of annexList) {
      try {
        const file = await loadAnnexureFile(annex);

        if (file.type === "image") {
          if (blocks.length > 0) blocks.push({ text: "", pageBreak: "after" });
          blocks.push({
            text: `Annexure ${imageIndex}: ${annex.label || "Untitled"}`,
            fontSize: 10,
            bold: true,
            margin: [0, 8, 0, 6],
          });
          blocks.push({
            image: file.data,
            width: 480,
            margin: [0, 4, 0, 12],
          });
          imageIndex++;
        } else if (file.type === "pdf") {
          const cleanBase64 = file.data.replace(
            "data:application/pdf;base64,",
            "",
          );
          annexurePdfList.push({
            fileName: annex.path,
            base64: cleanBase64,
          });
        } else {
          blocks.push({
            text: `Cannot load annexure (${file.error || "Unknown error"})`,
            color: "red",
            fontSize: 8,
            italics: true,
          });
        }
      } catch (err) {
        console.error("Annexure build error:", err);
        blocks.push({
          text: "Unexpected error loading annexure",
          color: "red",
          fontSize: 8,
          italics: true,
        });
      }
    }
    return blocks;
  };

  // ----------------------------
  // Logo
  // ----------------------------
  const imageUrl = await fetchImages();
  let base64Image = null;
  if (
    imageUrl &&
    typeof imageUrl === "object" &&
    typeof imageUrl.url === "string" &&
    imageUrl.url.startsWith("data:image/")
  ) {
    base64Image = imageUrl.url;
  }

  // ----------------------------
  // Factory details (section 5 only)
  // ----------------------------
  const factoryRows = [
    ["Inspection Officer", inspectionOfficerName],
    ["Designation", inspectionOfficerDesignation],
    ["Factory Name", auddetails.name],
    [
      "Factory Address",
      joinParts([auddetails.city, auddetails.dist, auddetails.state]),
    ],
    ["Licence No.", auddetails.licencenumber],
    ["Date of Inspection", observationdate],
    ["Occupier Name", industry.occupieName],
    [
      "Occupier Contact / Email",
      joinParts(
        [industry.occupierPhone, occ.occupierEmail || occ.email],
        " | ",
      ),
    ],
    ["Manager Name", industry.managerName],
    [
      "Manager Contact / Email",
      joinParts([industry.managerPhone, industry.managerEmail], " | "),
    ],
    ["Management Personnel Present", industry.managementper],
  ];

  // ----------------------------
  // Section IDs
  // ----------------------------
  const sectionIDs = {
    ack: "acknowledgement",
    exec: "executiveSummary",
    intro: "introduction",
    methodology: "methodology",
    factory: "factoryDetails",
    emp: "employeeSummary",
    obs: "observations",
    priority: "priorityRisk",
    closing: "closingMeeting",
    rec: "recommendation",
    conc: "conclusion",
    ref: "references",
    abb: "abbreviation",
    annex: "annexure",
    doc: "documentsSection",
  };

  const sectionHeader = (id, title) => ({
    id,
    text: title,
    fontSize: 11,
    bold: true,
    color: NAVY,
    margin: [0, 8, 0, 6],
  });

  // ----------------------------
  // docDefinition
  // ----------------------------
  const docDefinition = {
    pageSize: "A4",
    pageMargins: [32, 18, 32, 45],
    defaultStyle: { font: "Poppins", fontSize: 9 },

    background: (currentPage, pageSize) => ({
      canvas: [
        {
          type: "rect",
          x: 12,
          y: 12,
          w: pageSize.width - 24,
          h: pageSize.height - 24,
          lineWidth: 1.2,
          lineColor: NAVY,
        },
      ],
    }),

    footer: (currentPage, pageCount) => ({
      columns: [
        {
          text: `${orgData?.name || ""}`,
          fontSize: 7,
          color: "#666",
          margin: [20, 8, 0, 0],
        },
        {
          text: `Page ${currentPage} of ${pageCount}`,
          alignment: "right",
          fontSize: 7,
          color: "#666",
          margin: [0, 8, 20, 0],
        },
      ],
    }),

    pageBreakBefore: function (currentNode) {
      if (currentNode.id) markPage(currentNode.id, currentNode);
      return false;
    },

    content: [
      // ========== COVER – org details ==========
      {
        stack: [
          {
            text: "AUDIT OBSERVATION REPORT",
            alignment: "center",
            fontSize: 18,
            bold: true,
            color: NAVY,
            margin: [0, 25, 0, 8],
          },
          {
            table: {
              widths: ["50%", "50%"],
              body: [
                [
                  {
                    text: [
                      {
                        text: "Observation Date: ",
                        color: "#c62828",
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        text:
                          observationdate || new Date().toLocaleDateString(),
                        bold: true,
                        fontSize: 9,
                      },
                    ],
                  },
                  {
                    text: [
                      {
                        text: "Generated By: ",
                        color: "#c62828",
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        text: inspectionOfficerName || "System",
                        bold: true,
                        fontSize: 9,
                      },
                    ],
                    alignment: "right",
                  },
                ],
                [
                  {
                    text: [
                      {
                        text: "Division: ",
                        color: "#c62828",
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        text:
                          getLabelFromValue(auddetails?.divcode, divCdList) ||
                          "—",
                        bold: true,
                        fontSize: 9,
                      },
                    ],
                  },
                  {
                    text: [
                      {
                        text: "Licence No: ",
                        color: "#c62828",
                        bold: true,
                        fontSize: 9,
                      },
                      {
                        text: auddetails?.licencenumber || "—",
                        bold: true,
                        fontSize: 9,
                      },
                    ],
                    alignment: "right",
                  },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 0, 0, 16],
          },
          {
            stack: [
              ...(base64Image
                ? [{ image: base64Image, width: 100, alignment: "center" }]
                : []),
              ...(orgName
                ? [
                    {
                      text: orgName,
                      alignment: "center",
                      bold: true,
                      fontSize: 12,
                      color: "#c62828",
                      margin: [0, base64Image ? 10 : 40, 0, 4],
                    },
                  ]
                : []),
              ...(orgAddressLine
                ? [
                    {
                      text: orgAddressLine,
                      alignment: "center",
                      fontSize: 9,
                      color: "#444",
                      margin: [0, 1, 0, 1],
                    },
                  ]
                : []),
              ...(orgContactLine
                ? [
                    {
                      text: orgContactLine,
                      alignment: "center",
                      fontSize: 9,
                      margin: [0, 4, 0, 0],
                    },
                  ]
                : []),
            ],
            margin: [0, 10, 0, 0],
          },
        ],
        pageBreak: "after",
      },

      // ========== INDEX placeholder ==========
      {
        id: "indexPage",
        text: "INDEX PLACEHOLDER",
        pageBreak: "after",
        margin: [0, 10, 0, 10],
      },

      sectionHeader(sectionIDs.ack, "1. ACKNOWLEDGEMENT"),
      {
        text: ackContent || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.exec, "2. EXECUTIVE SUMMARY"),
      {
        text: exSummaryContent || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.intro, "3. INTRODUCTION"),
      {
        text: introductionContent || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.methodology, "4. METHODOLOGY"),
      {
        text: firstNote || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.factory, "5. FACTORY DETAILS"),
      {
        table: {
          widths: ["32%", "68%"],
          body: factoryRows.map(([label, value]) => [
            { text: label, bold: true, fontSize: 9 },
            { text: clean(value), fontSize: 9 },
          ]),
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => "#90a4ae",
          vLineColor: () => "#90a4ae",
          paddingLeft: () => 6,
          paddingRight: () => 6,
          paddingTop: () => 4,
          paddingBottom: () => 4,
        },
        margin: [0, 2, 0, 12],
      },

      sectionHeader(sectionIDs.emp, "6. EMPLOYEES SUMMARY"),
      {
        table: {
          widths: ["*", 70, 70],
          body: [
            [
              {
                text: "Employee Type",
                bold: true,
                fontSize: 9,
                fillColor: "#e8eaf6",
                margin: [3, 3, 3, 3],
              },
              {
                text: "Adult Male",
                bold: true,
                fontSize: 9,
                fillColor: "#e8eaf6",
                alignment: "center",
                margin: [3, 3, 3, 3],
              },
              {
                text: "Adult Female",
                bold: true,
                fontSize: 9,
                fillColor: "#e8eaf6",
                alignment: "center",
                margin: [3, 3, 3, 3],
              },
            ],
            ...employeeRows.map(([type, male, female]) => [
              { text: type || "—", fontSize: 9, margin: [3, 2, 3, 2] },
              {
                text: male || "0",
                fontSize: 9,
                alignment: "center",
                margin: [3, 2, 3, 2],
              },
              {
                text: female || "0",
                fontSize: 9,
                alignment: "center",
                margin: [3, 2, 3, 2],
              },
            ]),
          ],
        },
        layout: {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => "#b0bec5",
          vLineColor: () => "#b0bec5",
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 2, 0, 12],
      },

      sectionHeader(sectionIDs.obs, obsSectionTitle),
      { id: "observationsPlaceholder", text: "Loading observations..." },

      sectionHeader(sectionIDs.priority, "8. RISK PRIORITY CATEGORIES"),
      {
        text: PriorityContent || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.closing, "9. CLOSING MEETING"),
      {
        text: closingNotes || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.rec, "10. RECOMMENDATION"),
      {
        text: recommendation || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.conc, "11. CONCLUSION"),
      {
        text: conclusion || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.ref, "12. REFERENCES"),
      {
        text: references || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      sectionHeader(sectionIDs.abb, "13. ABBREVIATION"),
      {
        text: abbreviation || "",
        alignment: "justify",
        fontSize: 9,
        margin: [0, 0, 0, 10],
      },

      { text: "", pageBreak: "after" },
      sectionHeader(sectionIDs.annex, "14. ANNEXURE"),
      { id: "annexurePlaceholder", text: "Loading annexures..." },

      { text: "", pageBreak: "after" },
      sectionHeader(sectionIDs.doc, "15. DOCUMENTS"),
      { id: "documentsPlaceholder", text: "Loading documents..." },
    ],
  };

  // ----------------------------
  // Populate dynamic sections
  // ----------------------------
  try {
    const observationsBlocks = await getObservationsContent();
    const obsIndex = docDefinition.content.findIndex(
      (b) => b.id === "observationsPlaceholder",
    );
    if (obsIndex !== -1)
      docDefinition.content.splice(obsIndex, 1, ...observationsBlocks);
  } catch (err) {
    console.error("Failed to build observations:", err);
    const obsIndex = docDefinition.content.findIndex(
      (b) => b.id === "observationsPlaceholder",
    );
    if (obsIndex !== -1)
      docDefinition.content.splice(obsIndex, 1, {
        text: "Failed to load observations",
        color: "red",
        fontSize: 9,
      });
  }

  try {
    const annexureBlocks = await buildAnnexureContent(annextureList);
    const annexIndex = docDefinition.content.findIndex(
      (b) => b.id === "annexurePlaceholder",
    );
    if (annexIndex !== -1)
      docDefinition.content.splice(annexIndex, 1, ...annexureBlocks);
  } catch (err) {
    console.error("Failed to build annexures:", err);
    const annexIndex = docDefinition.content.findIndex(
      (b) => b.id === "annexurePlaceholder",
    );
    if (annexIndex !== -1)
      docDefinition.content.splice(annexIndex, 1, {
        text: "Failed to load annexures",
        color: "red",
        fontSize: 9,
      });
  }

  try {
    const docBlocks = await buildDocumentsSection(documents);
    const docIndex = docDefinition.content.findIndex(
      (b) => b.id === "documentsPlaceholder",
    );
    if (docIndex !== -1)
      docDefinition.content.splice(docIndex, 1, ...docBlocks);
    else docDefinition.content.push(...docBlocks);
  } catch (err) {
    console.error("Failed to build documents section:", err);
    const docIndex = docDefinition.content.findIndex(
      (b) => b.id === "documentsPlaceholder",
    );
    if (docIndex !== -1)
      docDefinition.content.splice(docIndex, 1, {
        text: "Failed to load documents",
        color: "red",
        fontSize: 9,
      });
  }

  // ----------------------------
  // First pass → Index
  // ----------------------------
  const firstPass = pdfMake.createPdf(docDefinition);
  await new Promise((resolve) => {
    firstPass.getBase64(() => resolve());
  });

  const indexRows = [
    ["1", "Acknowledgement", sectionPageMap[sectionIDs.ack] || ""],
    ["2", "Executive Summary", sectionPageMap[sectionIDs.exec] || ""],
    ["3", "Introduction", sectionPageMap[sectionIDs.intro] || ""],
    ["4", "Methodology", sectionPageMap[sectionIDs.methodology] || ""],
    ["5", "Factory Details", sectionPageMap[sectionIDs.factory] || ""],
    ["6", "Employees Summary", sectionPageMap[sectionIDs.emp] || ""],
    ["7", obsIndexLabel, sectionPageMap[sectionIDs.obs] || ""],
    ["8", "Risk Priority", sectionPageMap[sectionIDs.priority] || ""],
    ["9", "Closing Meeting", sectionPageMap[sectionIDs.closing] || ""],
    ["10", "Recommendation", sectionPageMap[sectionIDs.rec] || ""],
    ["11", "Conclusion", sectionPageMap[sectionIDs.conc] || ""],
    ["12", "References", sectionPageMap[sectionIDs.ref] || ""],
    ["13", "Abbreviation", sectionPageMap[sectionIDs.abb] || ""],
    ["14", "Annexure", sectionPageMap[sectionIDs.annex] || ""],
    ["15", "Documents", sectionPageMap[sectionIDs.doc] || ""],
  ];

  const indexHeaderCell = (text) => ({
    text,
    bold: true,
    fontSize: 9,
    alignment: "center",
    fillColor: NAVY,
    color: "white",
    margin: [2, 4, 2, 4],
  });

  const realIndexBlock = {
    unbreakable: true,
    pageBreak: "after",
    stack: [
      {
        text: "INDEX",
        alignment: "center",
        fontSize: 12,
        bold: true,
        color: NAVY,
        margin: [0, 6, 0, 8],
      },
      {
        table: {
          widths: ["12%", "68%", "20%"],
          body: [
            [
              indexHeaderCell("Sl. No."),
              indexHeaderCell("Content"),
              indexHeaderCell("Page"),
            ],
            ...indexRows.map((row, i) => [
              {
                text: row[0],
                fontSize: 9,
                alignment: "center",
                fillColor: i % 2 === 0 ? "#f5f5f5" : null,
                margin: [2, 3, 2, 3],
              },
              {
                text: row[1],
                fontSize: 9,
                fillColor: i % 2 === 0 ? "#f5f5f5" : null,
                margin: [4, 3, 4, 3],
              },
              {
                text: String(row[2]),
                fontSize: 9,
                alignment: "center",
                fillColor: i % 2 === 0 ? "#f5f5f5" : null,
                margin: [2, 3, 2, 3],
              },
            ]),
          ],
        },
        layout: {
          hLineWidth: () => 0.6,
          vLineWidth: () => 0.6,
          hLineColor: () => NAVY,
          vLineColor: () => "#90a4ae",
          paddingLeft: () => 4,
          paddingRight: () => 4,
          paddingTop: () => 2,
          paddingBottom: () => 2,
        },
        margin: [0, 4, 0, 10],
      },
    ],
  };

  const idxPos = docDefinition.content.findIndex((b) => b.id === "indexPage");
  if (idxPos !== -1) docDefinition.content.splice(idxPos, 1, realIndexBlock);

  const annexureStartPage = sectionPageMap[sectionIDs.annex];

  // ----------------------------
  // Generate + merge PDFs
  // ----------------------------
  return new Promise((resolve, reject) => {
    pdfMake.createPdf(docDefinition).getBase64(async (mainBase64) => {
      try {
        const mainPdfBytes = Uint8Array.from(atob(mainBase64), (c) =>
          c.charCodeAt(0),
        );
        const mainPdfDoc = await PDFDocument.load(mainPdfBytes);
        let insertAtIndex = annexureStartPage;

        for (const annex of annexurePdfList) {
          try {
            const annexPdfBytes = Uint8Array.from(atob(annex.base64), (c) =>
              c.charCodeAt(0),
            );
            const annexPdfDoc = await PDFDocument.load(annexPdfBytes);
            const copiedPages = await mainPdfDoc.copyPages(
              annexPdfDoc,
              annexPdfDoc.getPageIndices(),
            );
            for (const page of copiedPages) {
              mainPdfDoc.insertPage(insertAtIndex, page);
              insertAtIndex++;
            }
          } catch (err) {
            console.error(
              "Failed to insert annexure PDF:",
              annex.fileName,
              err,
            );
          }
        }

        let documentInsertAt = mainPdfDoc.getPageCount();
        const sortedPdfDocs = [...documentPdfList].sort(
          (a, b) => (Number(a.orderBy) || 0) - (Number(b.orderBy) || 0),
        );

        for (const doc of sortedPdfDocs) {
          try {
            const docPdfDoc = await PDFDocument.load(
              Uint8Array.from(atob(doc.base64), (c) => c.charCodeAt(0)),
            );
            const pages = await mainPdfDoc.copyPages(
              docPdfDoc,
              docPdfDoc.getPageIndices(),
            );
            for (const p of pages) {
              mainPdfDoc.insertPage(documentInsertAt, p);
              documentInsertAt++;
            }
          } catch (err) {
            console.error("Document merge error:", err);
          }
        }

        const finalPdfBytes = await mainPdfDoc.save();
        const blob = new Blob([finalPdfBytes], { type: "application/pdf" });
        resolve(blob);
      } catch (err) {
        console.error("PDF Merge Failed:", err);
        reject(err);
      }
    });
  });
};
